// AppRoutes.js - Añadir redirección para comerciales
import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import ProtectedRoute, { GuestRoute } from "./ProtectedRoute";
import AppLayout from "./components/layout/AppLayout";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import ClientsPage from "./pages/ClientsPage";
import ContactsPage from "./pages/ContactsPage";
import ProposalsPage from "./pages/ProposalsPage";
import ContractsPage from "./pages/ContractsPage";
import ProjectsPage from "./pages/ProjectsPage";
import SalesInvoicesPage from "./pages/SalesInvoicesPage";
import PurchaseInvoicesPage from "./pages/PurchaseInvoicesPage";
import ChatPage from "./pages/ChatPage";
import CompaniesPage from "./pages/CompaniesPage";
import NotFoundPage from "./pages/NotFoundPage";
import CalendarPage from "./pages/CalendarPage";
import TasksPage from "./pages/TasksPage";
import SuppliersPage from "./pages/SuppliersPage";
import CommercialsPage from "./pages/CommercialsPage";
import JournalEntriesPage from "./pages/accounting/JournalEntriesPage";
import ChartOfAccountsPage from "./pages/accounting/ChartOfAccountsPage";
import BalancePage from "./pages/accounting/BalancePage";
import GeneralLedgerPage from "./pages/accounting/GeneralLedgerPage";
import SettingsPage from "./pages/SettingsPage";
import SwitchSuppliersPage from "./pages/SwitchSuppliersPage";
import SwitchClientsPage from "./pages/SwitchClientsPage";
import SwitchesPage from "./pages/SwitchesPage";
import SwitchCalendarPage from "./pages/SwitchCalendarPage";

import InvoicesPage from "./pages/InvoicesPage";
import FinancialAnalysis from "./pages/FinancialAnalysis";
import { useAuth } from "./hooks/useAuth";
import { useCollection } from "./hooks/useCollection";

// Componente para redirigir comerciales
const CommercialRedirect = () => {
  const { userProfile } = useAuth();
  const { data: roles } = useCollection("roles");

  const isCommercial = React.useMemo(() => {
    if (!userProfile?.role || !roles) return false;
    const currentRole = roles.find(
      (r) => r.id === userProfile.role || r.name === userProfile.role,
    );
    if (!currentRole) return false;
    const roleName = currentRole.name?.toLowerCase() || "";
    return roleName.includes("comercial") || roleName.includes("commercial");
  }, [userProfile, roles]);

  if (isCommercial) {
    return <Navigate to="/proposals" replace />;
  }

  return <DashboardPage />;
};

// Componente guard para restringir acceso a comerciales
const CommercialGuard = ({ children }) => {
  const { userProfile } = useAuth();
  const { data: roles } = useCollection("roles");
  const location = useLocation();

  const isCommercial = React.useMemo(() => {
    if (!userProfile?.role || !roles) return false;
    const currentRole = roles.find(
      (r) => r.id === userProfile.role || r.name === userProfile.role,
    );
    if (!currentRole) return false;
    const roleName = currentRole.name?.toLowerCase() || "";
    return roleName.includes("comercial") || roleName.includes("commercial");
  }, [userProfile, roles]);

  // Si es comercial, redirigir a /proposals
  if (isCommercial) {
    return <Navigate to="/proposals" replace />;
  }

  return children;
};

export const AppRoutes = () => {
  return (
    <Routes>
      {/* Ruta pública */}
      <Route
        path="/login"
        element={
          <GuestRoute>
            <LoginPage />
          </GuestRoute>
        }
      />

      {/* Rutas protegidas CON layout (Header + Sidebar + MainContent) */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        {/* Dashboard con redirección para comerciales */}
        <Route index element={<CommercialRedirect />} />

        {/* Propuestas - Única ruta accesible para comerciales */}
        <Route path="proposals" element={<ProposalsPage />} />
        <Route path="proposals/:proposalId" element={<ProposalsPage />} />

        {/* Análisis Financiero - Protegido */}
        <Route 
          path="/financial-analysis" 
          element={
            <CommercialGuard>
              <FinancialAnalysis />
            </CommercialGuard>
          } 
        />

        {/* Configuración - Protegido */}
        <Route 
          path="/settings" 
          element={
            <CommercialGuard>
              <SettingsPage />
            </CommercialGuard>
          } 
        />
        <Route 
          path="/settings/roles" 
          element={
            <CommercialGuard>
              <SettingsPage />
            </CommercialGuard>
          } 
        />
        <Route 
          path="/settings/permissions" 
          element={
            <CommercialGuard>
              <SettingsPage />
            </CommercialGuard>
          } 
        />

        {/* Grupo Contactos - Protegido */}
        <Route 
          path="clients" 
          element={
            <CommercialGuard>
              <ClientsPage />
            </CommercialGuard>
          } 
        />
        <Route 
          path="clients/:clientId" 
          element={
            <CommercialGuard>
              <ClientsPage />
            </CommercialGuard>
          } 
        />
        <Route 
          path="contacts" 
          element={
            <CommercialGuard>
              <ContactsPage />
            </CommercialGuard>
          } 
        />
        <Route 
          path="contacts/:contactId" 
          element={
            <CommercialGuard>
              <ContactsPage />
            </CommercialGuard>
          } 
        />
        <Route 
          path="suppliers" 
          element={
            <CommercialGuard>
              <SuppliersPage />
            </CommercialGuard>
          } 
        />
        <Route 
          path="suppliers/:supplierId" 
          element={
            <CommercialGuard>
              <SuppliersPage />
            </CommercialGuard>
          } 
        />
        <Route 
          path="commercials" 
          element={
            <CommercialGuard>
              <CommercialsPage />
            </CommercialGuard>
          } 
        />
        <Route 
          path="commercials/:commercialId" 
          element={
            <CommercialGuard>
              <CommercialsPage />
            </CommercialGuard>
          } 
        />

        {/* Grupo Proyectos - Solo Propuestas accesible para comerciales */}
        <Route 
          path="contracts" 
          element={
            <CommercialGuard>
              <ContractsPage />
            </CommercialGuard>
          } 
        />
        <Route 
          path="contracts/:contractId" 
          element={
            <CommercialGuard>
              <ContractsPage />
            </CommercialGuard>
          } 
        />
        <Route 
          path="projects" 
          element={
            <CommercialGuard>
              <ProjectsPage />
            </CommercialGuard>
          } 
        />
        <Route 
          path="projects/:projectId" 
          element={
            <CommercialGuard>
              <ProjectsPage />
            </CommercialGuard>
          } 
        />

        {/* Módulo Finanzas - Ventas / Ingresos - Protegido */}
        <Route 
          path="sales" 
          element={
            <CommercialGuard>
              <SalesInvoicesPage />
            </CommercialGuard>
          } 
        />
        <Route 
          path="sales/:invoiceId" 
          element={
            <CommercialGuard>
              <SalesInvoicesPage />
            </CommercialGuard>
          } 
        />

        {/* Módulo Finanzas - Compras / Gastos - Protegido */}
        <Route 
          path="purchases" 
          element={
            <CommercialGuard>
              <PurchaseInvoicesPage />
            </CommercialGuard>
          } 
        />
        <Route 
          path="purchases/:invoiceId" 
          element={
            <CommercialGuard>
              <PurchaseInvoicesPage />
            </CommercialGuard>
          } 
        />

        {/* Módulo Finanzas - Facturas de Venta (EPC → Cliente) - Protegido */}
        <Route 
          path="invoices" 
          element={
            <CommercialGuard>
              <InvoicesPage />
            </CommercialGuard>
          } 
        />
        <Route 
          path="invoices/:invoiceId" 
          element={
            <CommercialGuard>
              <InvoicesPage />
            </CommercialGuard>
          } 
        />

        {/* Grupo Agenda - Protegido */}
        <Route 
          path="calendar" 
          element={
            <CommercialGuard>
              <CalendarPage />
            </CommercialGuard>
          } 
        />
        <Route 
          path="calendar/:eventId" 
          element={
            <CommercialGuard>
              <CalendarPage />
            </CommercialGuard>
          } 
        />
        <Route 
          path="tasks" 
          element={
            <CommercialGuard>
              <TasksPage />
            </CommercialGuard>
          } 
        />
        <Route 
          path="tasks/:taskId" 
          element={
            <CommercialGuard>
              <TasksPage />
            </CommercialGuard>
          } 
        />

        {/* Grupo Comercializadora - Protegido */}
        <Route 
          path="/switch-clients" 
          element={
            <CommercialGuard>
              <SwitchClientsPage />
            </CommercialGuard>
          } 
        />
        <Route 
          path="/switch-suppliers" 
          element={
            <CommercialGuard>
              <SwitchSuppliersPage />
            </CommercialGuard>
          } 
        />
        <Route 
          path="/switches" 
          element={
            <CommercialGuard>
              <SwitchesPage />
            </CommercialGuard>
          } 
        />
        <Route 
          path="/switch-calendar" 
          element={
            <CommercialGuard>
              <SwitchCalendarPage />
            </CommercialGuard>
          } 
        />

        {/* Otros - Protegido */}
        <Route 
          path="chat" 
          element={
            <CommercialGuard>
              <ChatPage />
            </CommercialGuard>
          } 
        />
        <Route 
          path="chat/:roomId" 
          element={
            <CommercialGuard>
              <ChatPage />
            </CommercialGuard>
          } 
        />
        <Route 
          path="companies" 
          element={
            <CommercialGuard>
              <CompaniesPage />
            </CommercialGuard>
          } 
        />
        <Route 
          path="companies/:companyId" 
          element={
            <CommercialGuard>
              <CompaniesPage />
            </CommercialGuard>
          } 
        />

        {/* Contabilidad - Protegido */}
        <Route 
          path="accounting/journal" 
          element={
            <CommercialGuard>
              <JournalEntriesPage />
            </CommercialGuard>
          } 
        />
        <Route
          path="accounting/journal/:entryId"
          element={
            <CommercialGuard>
              <JournalEntriesPage />
            </CommercialGuard>
          }
        />
        <Route
          path="accounting/chart-of-accounts"
          element={
            <CommercialGuard>
              <ChartOfAccountsPage />
            </CommercialGuard>
          }
        />
        <Route
          path="accounting/general-ledger"
          element={
            <CommercialGuard>
              <GeneralLedgerPage />
            </CommercialGuard>
          }
        />
        <Route 
          path="accounting/balance" 
          element={
            <CommercialGuard>
              <BalancePage />
            </CommercialGuard>
          } 
        />
      </Route>

      {/* Admin */}
      <Route
        path="/admin/*"
        element={
          <ProtectedRoute requiredRoles={["admin"]}>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route path="users" element={<div>Admin Users Page</div>} />
        <Route path="settings" element={<div>Admin Settings Page</div>} />
      </Route>

      {/* 404 */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};